import React from 'react'
import { Row, Col, Card, Table, Tag, Button, Tabs, Alert, Space, Badge } from 'antd'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts'
import { STOCK_STATUS, EXPIRY_STATUS, COLORS } from '../InventoryItems/utils/constants'
import { getStockStatus, getExpiryStatus, formatCurrency, formatDate } from '../InventoryItems/utils/helpers'

const { TabPane } = Tabs

const AlertsAndTables = ({ alertItems, pendingOrders, departmentData }) => {
  // Chart colors
  const CHART_COLORS = [
    '#1890ff',
    '#13c2c2',
    '#52c41a',
    '#faad14',
    '#f5222d',
    '#722ed1',
    '#eb2f96',
    '#fa8c16',
    '#a0d911',
    '#fadb14'
  ]

  // Render alert items table
  const alertColumns = [
    {
      title: 'Item',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          {record.quantityOnHand < record.minStockLevel && <Badge color={COLORS.LOW_STOCK} />}
          {record.expiryDate && getExpiryStatus(record.expiryDate).status !== 'Valid' && (
            <Badge color={COLORS.EXPIRED} />
          )}
          {text}
        </Space>
      )
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category'
    },
    {
      title: 'Stock',
      dataIndex: 'quantityOnHand',
      key: 'quantityOnHand',
      render: (qty, record) => {
        const status = getStockStatus(record)
        return (
          <Tag color={status.color}>
            {qty} / Min: {record.minStockLevel}
          </Tag>
        )
      }
    },
    {
      title: 'Expiry',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      render: (date) => {
        if (!date) return 'N/A'
        const status = getExpiryStatus(date)
        return (
          <Tag color={status.color}>
            {formatDate(date)} - {status.status}
          </Tag>
        )
      }
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button size='small' type='primary'>
          Order
        </Button>
      )
    }
  ]

  // Render pending orders table
  const orderColumns = [
    {
      title: 'Order ID',
      dataIndex: 'id',
      key: 'id'
    },
    {
      title: 'Supplier',
      dataIndex: 'supplierName',
      key: 'supplierName'
    },
    {
      title: 'Date',
      dataIndex: 'orderDate',
      key: 'orderDate',
      render: (date) => formatDate(date)
    },
    {
      title: 'Total',
      dataIndex: 'totalCost',
      key: 'totalCost',
      render: (value) => formatCurrency(value)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let color = 'default'
        if (status === 'Pending') color = 'orange'
        if (status === 'Approved') color = 'blue'
        if (status === 'Received') color = 'green'

        return <Tag color={color}>{status}</Tag>
      }
    }
  ]

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24}>
        <Card>
          <Tabs defaultActiveKey='1' className='dashboard-tabs'>
            <TabPane tab='Alerts' key='1'>
              {alertItems.length === 0 ? (
                <Alert
                  message='No alerts'
                  description='All inventory items are at healthy levels and within expiry dates.'
                  type='success'
                  showIcon
                />
              ) : (
                <Table
                  dataSource={alertItems}
                  columns={alertColumns}
                  rowKey='id'
                  pagination={{ pageSize: 5 }}
                  className='inventory-table'
                />
              )}
            </TabPane>
            <TabPane tab='Pending Orders' key='2'>
              {pendingOrders.length === 0 ? (
                <Alert
                  message='No pending orders'
                  description='There are no pending purchase orders at this time.'
                  type='info'
                  showIcon
                />
              ) : (
                <Table
                  dataSource={pendingOrders}
                  columns={orderColumns}
                  rowKey='id'
                  pagination={{ pageSize: 5 }}
                  className='inventory-table'
                />
              )}
            </TabPane>
            <TabPane tab='Departments' key='3'>
              <ResponsiveContainer width='100%' height={300}>
                <BarChart data={departmentData}>
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis dataKey='name' />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey='value' name='Item Count' fill={COLORS.PRIMARY}>
                    {departmentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </TabPane>
          </Tabs>
        </Card>
      </Col>
    </Row>
  )
}

export default AlertsAndTables
